import { Toolbar } from '@pogokid/mui'
import React, { FC } from 'react'
import styles from './TopToolbar.module.css'
import { Text } from '~m/designSystem'
import { QuickSearchButton } from '../QuickSearchButton'
import { useScreenTitle } from '~m/AppUi/context'

interface TopToolbarProps {
  heading?: string
  /**
   * Opens the global search. When given, the heading shows inside
   * the search button. Without it the heading shows as plain text.
   */
  onSearch?: () => void
}

/**
 * Sticky bar at the top of a page.
 *
 * The search button (holding the page heading) is centred in the
 * space left of the actions (`children`), which are right-aligned.
 */
export const TopToolbar: FC<
  React.PropsWithChildren<TopToolbarProps>
> = ({ heading, onSearch, children }) => {
  const screenTitle = useScreenTitle()
  const title = screenTitle ?? heading
  return (
    <Toolbar
      className={styles.TopToolbar}
      disableGutters={false}
      data-testid="top-toolbar"
    >
      {onSearch ? (
        <div className={styles.TopToolbar__search}>
          {/* The heading shows in the search button. This keeps
              it as the page's h1 for screen readers. */}
          {title && (
            <Text
              variant="page-title"
              className={styles.TopToolbar__hiddenHeading}
            >
              {title}
            </Text>
          )}
          <QuickSearchButton title={title} onClick={onSearch} />
        </div>
      ) : (
        heading && (
          <Text
            variant="page-title"
            className={styles.TopToolbar__heading}
            data-testid="top-toolbar__heading"
          >
            {title}
          </Text>
        )
      )}
      <div className={styles.TopToolbar__actions}>
        {children}
      </div>
    </Toolbar>
  )
}
